/**
 * Documents global error boundary behavior for ERP Accelerator.
 */

'use client';

import { useEffect } from 'react';

/**
 * Handles global error boundary behavior.
 *
 * Rendered when the root layout itself fails, so it must provide its own
 * <html>/<body> and cannot rely on CSS variables from globals.css.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <html lang="en">
      <body>
        <style>{`
          .global-error-root {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;
            background: #f6f8f8;
            font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
          }
          .global-error-card {
            max-width: 420px;
            width: 100%;
            background: #ffffff;
            border: 1px solid #e2e8e7;
            border-radius: 12px;
            padding: 32px;
            text-align: center;
            box-shadow: 0 8px 24px -8px rgba(16, 59, 53, 0.18);
          }
          .global-error-title {
            margin: 0 0 8px;
            font-size: 20px;
            font-weight: 700;
            color: #103b35;
          }
          .global-error-text {
            margin: 0 0 24px;
            font-size: 14px;
            line-height: 1.5;
            color: #5f6f6c;
          }
          .global-error-button {
            display: inline-block;
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            background: #13755F;
            color: #ffffff;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
          }
          .global-error-button:hover {
            background: #0f5f4d;
          }
        `}</style>
        <div className="global-error-root">
          <div className="global-error-card">
            <h1 className="global-error-title">Something went wrong</h1>
            <p className="global-error-text">
              An unexpected error occurred. Please try again.
            </p>
            <button className="global-error-button" onClick={() => reset()}>
              Try again
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
